<script setup>
    import { ref, onMounted, computed } from "vue";
    import { useStore } from 'vuex';
    import CreatePermission from "@/components/modals/CreatePermission.vue";
    import ConfirmModal from "@/components/modals/ConfirmModal.vue";
    import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';
    import GenericTable2 from '@/components/tables/GenericTable2.vue';
    import Roles from '@/api/roles';

    // Grab stored values
    const store = useStore();
    const user = store.state.user;

    // Permission checks
    // Having any role-related permission grants access to view the page
    const hasAccess = computed(() => {
        return user && (
            user.role === 'admin' || 
            (user.permissions && (
                user.permissions.includes('viewRoles') ||
                user.permissions.includes('createRole') ||
                user.permissions.includes('editRole') ||
                user.permissions.includes('deleteRole')
            ))
        );
    });
    
    const canCreateRole = computed(() => {
        return user && (user.role === 'admin' || (user.permissions && user.permissions.includes('createRole')));
    });
    
    const canEditRole = computed(() => {
        return user && (user.role === 'admin' || (user.permissions && user.permissions.includes('editRole')));
    });
    
    const canDeleteRole = computed(() => {
        return user && (user.role === 'admin' || (user.permissions && user.permissions.includes('deleteRole')));
    });
    
    // Build actions array based on permissions
    const availableActions = computed(() => {
        const actions = [];
        if (canEditRole.value) actions.push('edit');
        if (canDeleteRole.value) actions.push('delete');
        return actions;
    });

    const roles = ref([]);
    const tableData = ref([]);
    const loading = ref(true);
    const modal = ref(null);
    const selectedRole = ref({});
    const roleToDelete = ref(null);
    const deleteLoading = ref(false);
    const deleteError = ref('');

    onMounted(async () => {
        await loadRoles();
    });

    const loadRoles = async () => {
        loading.value = true;
        try {
            const response = await Roles.get();
            roles.value = response.roles;

            tableData.value = roles.value.map(role => ({
                id: role.id,
                Name: role.id.replace(/-/g, ' '),
                Permissions: role.permissions ? role.permissions.length : (role.id === 'admin' ? 'All' : 0),
                Description: role.description || '--',
            }));
        } catch (error) {
            console.error('Error loading roles:', error);
        } finally {
            loading.value = false;
        }
    };

    const handleRoleCreated = (roleData) => {
        
        // Add the new role to the list locally
        roles.value.push(roleData);
        tableData.value.push({
            id: roleData.id,
            Name: roleData.name,
            Permissions: roleData.permissions ? roleData.permissions.length : 0,
            Description: roleData.description || '--',
        });
        
        modal.value = null;
    };

    const handleRoleUpdated = (roleData) => {
        
        // Update the role in the list locally
        const roleIndex = roles.value.findIndex(r => r.id === roleData.id);
        if (roleIndex !== -1) {
            roles.value[roleIndex] = roleData;
            tableData.value[roleIndex] = {
                id: roleData.id,
                Name: roleData.id.replace(/-/g, ' '),
                Permissions: roleData.permissions ? roleData.permissions.length : 0,
                Description: roleData.description || '--',
            };
        }
        
        modal.value = null;
    };

    const handleActionSelected = (actionData) => {
        const { action, row } = actionData;
        const role = roles.value.find(r => r.id === row.id);
        
        if (action === 'edit' && role) {
            selectedRole.value = role;
            modal.value = 'editRoleModal';
        } else if (action === 'delete' && role) {
            roleToDelete.value = role;
            deleteError.value = '';
            modal.value = 'deleteRoleModal';
        }
    };

    const confirmDeleteRole = async () => {
        if (!roleToDelete.value) return;
        deleteLoading.value = true;
        deleteError.value = '';
        try {
            await Roles.delete(`/${roleToDelete.value.id}`);
            const roleIndex = roles.value.findIndex(r => r.id === roleToDelete.value.id);
            if (roleIndex !== -1) {
                roles.value.splice(roleIndex, 1);
                tableData.value.splice(roleIndex, 1);
            }
            modal.value = null;
            roleToDelete.value = null;
        } catch (error) {
            console.error('Error deleting role:', error);
            deleteError.value = error.message || 'Failed to delete role.';
        } finally {
            deleteLoading.value = false;
        }
    };

    const cancelDeleteRole = () => {
        modal.value = null;
        roleToDelete.value = null;
        deleteError.value = '';
    };
</script>

<template>
    <div v-if="!hasAccess" class="flex flex-col items-center justify-center h-full">
        <h1 class="text-3xl md:text-5xl my-4 md:my-6 font-bold text-mc-red">Access Denied</h1>
        <p class="text-xl text-ink-muted dark:text-on-dark-muted">You do not have permission to access this page.</p>
        <p class="text-lg text-ink-muted dark:text-on-dark-muted mt-2">Required permission: <span class="font-mono bg-mc-steel/30 dark:bg-white/10 px-2 py-1 rounded-mc-sm text-mc-ink dark:text-on-dark">viewRoles</span> or admin role</p>
    </div>
    <div v-else>
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <h1 class="text-2xl font-bold text-mc-ink dark:text-on-dark mt-4">Roles</h1>
            <button 
                v-if="canCreateRole"
                @click="modal = 'createRoleModal'; selectedRole = {}" 
                class="default-button w-fit"
            >
                + Create Custom Role
            </button>
        </div>

        <div v-if="loading" class="flex flex-col items-center mt-10">
            <LoadingAnimation3 message="Loading roles..." />
        </div>

        <div v-else class="space-y-4 border border-mc-steel dark:border-line-on-dark rounded-mc-md p-4">
            <GenericTable2
                :data="tableData"
                :showId="false"
                :actions="availableActions"
                :actionException="['admin', 'viewer']"
                :selectable="false"
                :pagination="true"
                :responsive="true"
                @ActionSelected="handleActionSelected"
            />
        </div>

        <CreatePermission 
            v-if="modal === 'createRoleModal'" 
            :roleDetails="{}"
            @close="modal = null; selectedRole = {}"
            @role-created="handleRoleCreated"
        />
        
        <CreatePermission
            v-if="modal === 'editRoleModal'"
            :roleDetails="selectedRole"
            @close="modal = null; selectedRole = {}"
            @role-updated="handleRoleUpdated"
        />

        <ConfirmModal
            v-if="modal === 'deleteRoleModal'"
            title="Delete Role"
            :message="`All users with this role will be assigned the &quot;viewer&quot; role. Are you sure you want to delete &quot;${roleToDelete?.id?.replace(/-/g, ' ')}&quot;?`"
            confirmLabel="Delete"
            variant="danger"
            :loading="deleteLoading"
            :error="deleteError"
            @confirm="confirmDeleteRole"
            @cancel="cancelDeleteRole"
        />
    </div>
</template>
